import { Inject, Injectable } from '@angular/core';
import { concatMap, from, Observable, of, map, catchError, first } from 'rxjs';
import {
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import { environment } from '../configuration/get-environment.util';
import { OnboardingProgress } from '../models/onboarding-progress.interface';
import { ANGULAR_HTTP_CLIENT_TOKEN } from '../angular-http-client.module';
import { HttpClient, HttpParams } from '@angular/common/http';

/**
 * Service responsible for tracking and retrieving user progress through onboarding sequences.
 * Communicates with the user flow proxy API to fetch progress data.
 */
/** @deprecated use @trustedshops/tswp-core-masterdata/OnboardingService instead */

@Injectable()
export class UserFlowProgressService {
  private static readonly TYPE: string = 'UserFlowProgressService';

  private readonly baseUrl: string = environment.userFlow.proxyApi;

  public constructor(
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService,
    @Inject(ANGULAR_HTTP_CLIENT_TOKEN)
    private readonly httpClient: HttpClient
  ) {}

  /**
   * Retrieves the progress for a specific onboarding content and user.
   *
   * @param contentId - The identifier for the onboarding content
   * @param userId - The identifier for the user
   * @returns An Observable emitting the onboarding progress information
   * or a default progress (0%) in case of an error
   */
  getOnboardingProgress(
    contentId: string,
    userId: string
  ): Observable<OnboardingProgress> {
    const params = new HttpParams({
      fromObject: {
        content_id: contentId,
        user_id: userId,
      },
    });

    return this.httpClient
      .get<OnboardingProgress>(`${this.baseUrl}/progress`, {
        params,
      })
      .pipe(
        map((response) => ({
          progress: this.toPercentage(response.progress || 0),
          contentId,
        })),
        catchError((error) => {
          this.logService.error(
            UserFlowProgressService.TYPE,
            new Error(
              `Error fetching progress for content ${contentId} and user ${userId}: ${JSON.stringify(
                error
              )}`
            )
          );

          return of({ progress: 0, contentId });
        })
      );
  }

  /**
   * Finds the first incomplete onboarding content from a list of content IDs.
   * An onboarding is considered incomplete if its progress is less than 100%.
   *
   * @param contentIds - Array of onboarding content identifiers to check
   * @param userId - The identifier for the user
   * @returns An Observable emitting the first incomplete onboarding progress or null
   * if all are complete, inputs are invalid, or an error occurs
   */
  getFirstIncompleteOnboarding(
    contentIds: string[],
    userId: string
  ): Observable<OnboardingProgress | null> {
    if (!contentIds || contentIds.length === 0 || !userId) {
      return of(null);
    }

    return from(contentIds).pipe(
      concatMap((id) => this.getOnboardingProgress(id, userId)),
      first((content) => content.progress < 100, null)
    );
  }

  /**
   * Converts a decimal value to a percentage and rounds to the nearest integer.
   * For example, 0.75 becomes 75.
   *
   * @param value - The decimal value to convert to percentage (0-1)
   * @returns The value converted to a percentage (0-100)
   */
  private toPercentage(value: number): number {
    return Math.round(value * 100);
  }
}
